import type { CurrentTimeButton, DynamicFieldDataProps, DynamicFormField, JsonDataItem } from '@myosh/odin-components';
import { DynamicFormRequiredType } from '@myosh/odin-components';
import { isEmpty } from 'lodash';
import type { Caption } from '../@types/common';
import type {
  CombinedFieldType,
  DynamicFormSettings,
  DynamicFormWorkflowStep,
  ExtendedDynamicFormField,
  ExtendedDynamicFormSettings,
  ExtendedDynamicFormWorkflowStep,
} from '../@types/forms';
import type { FormHierarchyTypes, TransformedFormHierarchyTypes } from '../@types/hierarchy-fields';
import type { ViewConfigDateRange } from '../@types/views';
import i18next from '../i18n';
import { forceAssert } from './type-guards';
import { doubleNumberPattern, intNumberPattern, maxNumberLengthPattern } from './validation-utils';

export const dataProps: DynamicFieldDataProps = {
  valueField: 'id',
  textField: 'caption',
  archivedField: 'archived',
};

function getDisplayType(displayType: 'EXPAND' | 'COLLAPSE'): boolean {
  return displayType !== 'COLLAPSE';
}

const transformAndSortDynamicFormHierarchyTypes = (hierarchyTypes?: Array<FormHierarchyTypes>) => {
  return hierarchyTypes
    ?.map((hierarchy) => {
      return { ...hierarchy, caption: hierarchy.caption.translations[0].value };
    })
    ?.sort((a, b) => a.weight - b.weight);
};

export function processWorkflowSteps(steps: Array<DynamicFormWorkflowStep>) {
  const convertedWorkflowSteps: Array<ExtendedDynamicFormWorkflowStep> = [];

  for (let i = 0, length = steps.length; i < length; i++) {
    const currentStep = steps[i];

    //FIXME: Once the API provides us with the users translation change this code
    convertedWorkflowSteps.push({
      id: currentStep.id,
      label: currentStep.label.translations[0].value,
      draft: currentStep.draft || false,
      rolesPermissions: currentStep.rolesPermissions,
      editModeByDefault: currentStep.editModeByDefault,
      hideRecordSaveButton: currentStep.hideRecordSaveButton,
      archived: currentStep.archived,
      nextSteps: currentStep.nextSteps?.map((nextStep) => {
        //FIXME: Once the API provides us with the users translation change this code
        return {
          id: nextStep.id,
          label: nextStep.label.translations[0].value,
          archivedStep: nextStep.archivedStep,
          buttonCaption: nextStep.buttonCaption?.translations[0].value,
          visibilityConditions: nextStep.nextStepVisibilities,
          closeOnSave: nextStep.closeOnSave,
          promptBeforeAdvance: nextStep.promptBeforeAdvance,
          skipMandatoryFieldValidation: nextStep.skipMandatoryFieldValidation,
          submissionBehaviour: nextStep.submissionBehaviour,
          navigationUrl: nextStep.navigationUrl,
        };
      }),
      backgroundColor: currentStep.backgroundColor,
    });
  }

  return convertedWorkflowSteps;
}

export function settingsToDynamicForm(settings: DynamicFormSettings) {
  //FIXME: Once the API provides us with the users translation change the caption line
  const formSettings: ExtendedDynamicFormSettings = {
    id: settings.id,
    caption:
      typeof settings.formCaption === 'string' ? settings.formCaption : settings.formCaption.translations[0].value,
    fields: [],
    globalProperties: settings.globalProperties ?? {},
    visibilityConditions: [],
    initialStep: settings?.initialStep,
    hierarchySettings: {
      hierarchyTypes: transformAndSortDynamicFormHierarchyTypes(
        settings?.hierarchyTypes
      ) as Array<TransformedFormHierarchyTypes>,
      isExpanded: getDisplayType(settings.hierarchyDisplayType ?? 'EXPAND'),
      hiddenInReadMode: settings.hierarchyHiddenInReadMode,
      enableAdvance: settings.hierarchyEnableAdvance,
      autoPopulateFromUserHierarchy: settings.autoPopulateHierarchyFromUserHierarchy,
      autoPopulateHierarchyFromPersonFieldId: settings.autoPopulateHierarchyFromPersonFieldId,
      hierarchyAccessRestrictionIgnoreMode: settings.hierarchyAccessRestrictionIgnoreMode,
    },
    dateRange: settings.dateRange as ViewConfigDateRange,
    displayRecordListColour: settings.displayRecordListColour,
    accessRights: {},
    hideFirstColumn: settings.hideFirstColumn,
    keywords: settings.keywords,
    questionRecordListFields: settings.questionRecordListFields,
    versionType: settings.versionType,
    notificationBadgeAlertsEnabled: settings.notificationBadgeAlertsEnabled,
    enableExternalUserSignUp: settings.enableExternalUserSignUp,
    enableCalendarView: settings.enableCalendarView,
  };

  for (let i = 0, length = settings.fieldGroups.length; i < length; i++) {
    const fieldGroup = settings.fieldGroups[i];

    if (fieldGroup.sectionVisibilities) {
      formSettings.visibilityConditions?.push({
        id: fieldGroup.id,
        type: 'GROUP',
        groups: [
          {
            conditions: fieldGroup.sectionVisibilities,
          },
        ],
      });
    }

    if (fieldGroup.accessRights && formSettings.accessRights) {
      formSettings.accessRights[fieldGroup.id] = fieldGroup.accessRights;
    }

    //FIXME: Once the API provides us with the users translation change this code
    formSettings.fields.push({
      id: fieldGroup.id,
      caption: typeof fieldGroup.caption === 'string' ? fieldGroup.caption : fieldGroup.caption.translations[0].value,
      fieldName: String(fieldGroup.id),
      fieldType: 'GROUP',
      position: i,
      startOpen: getDisplayType(fieldGroup.displayType),
      fields: [],
    });
    const groupField = formSettings.fields[i];

    if (fieldGroup.fields) {
      for (let j = 0, fLength = fieldGroup.fields.length; j < fLength; j++) {
        const fieldGroupField: ExtendedDynamicFormField = fieldGroup.fields[j] as ExtendedDynamicFormField;
        //FIXME: Once the API provides us with the users translation change the caption, helpMessage and hintMessage code
        const field: DynamicFormField = {
          id: fieldGroupField.id,
          parentFieldId: fieldGroupField.parentFieldId?.toString(),
          caption:
            typeof fieldGroupField.fieldCaption === 'string'
              ? fieldGroupField.fieldCaption
              : fieldGroupField.fieldCaption.translations[0].value,
          fieldName: String(fieldGroupField.id),
          fieldType: fieldGroupField.fieldType,
          position: fieldGroupField.position,
          dataSettings: dataProps,
          required: fieldGroupField.required ? DynamicFormRequiredType.True : DynamicFormRequiredType.False,
          helpMessage: fieldGroupField?.help?.translations[0]?.value,
          hintMessage: fieldGroupField?.hint?.translations[0]?.value,
          importEnabled: fieldGroupField?.importEnabled,
          lockDown: fieldGroupField?.lockDown,
        };

        const fieldType: CombinedFieldType = fieldGroupField.fieldType as CombinedFieldType;

        if ('LABEL' === fieldType && fieldGroupField.fieldData && !isEmpty(fieldGroupField.fieldData.translations)) {
          field.fieldData = fieldGroupField.fieldData.translations[0].value ?? '';
        }

        if ('OPTION_BUILDER' === fieldType) {
          field.dataSettings = {
            textField: 'text',
            valueField: 'value',
          };
        }

        if ('OPTIONGROUP' === fieldType && fieldGroupField.optionGroupOrientation) {
          field.dynamicProperties = { orientation: fieldGroupField.optionGroupOrientation };
          // modify type to the 'record' specific optiongroup
          (field.fieldType as unknown as CombinedFieldType) = 'RECORD_OPTIONGROUP';
        }

        if (
          ('RECORDLINK' === fieldType || 'USER_RECORDLINK' === fieldType || 'REVERSE_RECORD_LINK' === fieldType) &&
          fieldGroupField.recordLinkFormat &&
          !(fieldGroupField.questions || fieldGroupField.answers)
        ) {
          //FIXME: Once the API provides us with the users translation change the buttonCaption code
          field.dynamicProperties = {
            autoPopulateEnabled: fieldGroupField.autoPopulateEnabled,
            recordLinkFormat: fieldGroupField.recordLinkFormat,
            buttonCaption: fieldGroupField.buttonCaption?.translations[0].value,
            targetModuleFormId: fieldGroupField.targetModuleFormId,
            targetModuleId: fieldGroupField.targetModuleId,
            showSelectButton: fieldGroupField.showSelectButton,
            createExternalUsers: fieldGroupField.createExternalUsers,
            recordLinkRecordCreationDisabled: fieldGroupField.recordLinkRecordCreationDisabled,
            recordLinkType: fieldGroupField.recordLinkType,
            sourceModuleFormId: fieldGroupField.sourceModuleFormId,
            sourceModuleId: fieldGroupField.sourceModuleId,
          };
        }

        if ('SUMMARY_RECORD_LINK' === fieldType && fieldGroupField.fieldsToInclude) {
          field.dynamicProperties = {
            fieldsToInclude: fieldGroupField.fieldsToInclude,
          };
        }

        if ('COMBOBOX' === fieldType) {
          field.dynamicProperties = {
            deselectValue: {
              [dataProps.valueField]: 0,
              [dataProps.textField]: '',
              noSelectionValue: true,
            },
            showAddNewOption: fieldGroupField.allowNewValue,
          };

          // modify type to the 'record' specific optiongroup
          (field.fieldType as unknown as CombinedFieldType) = 'RECORD_COMBOBOX';
        }

        if ('PERSONFIELD' === fieldType) {
          field.dynamicProperties = {
            defaultFieldConfiguration: fieldGroupField.defaultFieldConfiguration,
            showPersonsManager: fieldGroupField.showPersonsManager,
            showPersonsOccupations: fieldGroupField.showPersonsOccupations,
          };
        }

        if ('READERS' === fieldType) {
          field.dynamicProperties = {
            readersExemptGroups: fieldGroupField.readersExemptGroups,
          };
        }

        if ('TEXTAREA' === fieldGroupField.fieldType) {
          field.dynamicProperties = {
            rows: fieldGroupField?.numberOfLines,
          };
        }

        if ('DATEFIELD' === fieldType || 'TIMEFIELD' === fieldType) {
          //"defaultValue": "[DATEFIELD+COMPETENCY]"
          if (fieldGroupField.defaultFieldConfiguration?.defaultValue === '[DATEFIELD+COMPETENCY]') {
            field.fieldType = 'DATEFIELD_COMPETENCY';
          }

          field.dynamicProperties = {
            currentTimeBtn: { caption: i18next.t('now') } as CurrentTimeButton,
            defaultFieldConfiguration: fieldGroupField.defaultFieldConfiguration,
            fieldValidations: fieldGroupField.fieldValidations?.[0],
            ignoreUserTimezone: fieldGroupField.ignoreUserTimezone,
          };
        }
        if ('INTFIELD' === fieldType) {
          field.dynamicProperties = {
            validations: [
              {
                pattern: intNumberPattern,
                errorLabel: i18next.t('integer-validation-message'),
              },
              {
                pattern: maxNumberLengthPattern,
                errorLabel: i18next.t('integer-max-length-message', { maxLength: 10 }),
              },
            ],
          };
        }

        if ('DOUBLEFIELD' === fieldType) {
          field.dynamicProperties = {
            validations: [
              {
                pattern: doubleNumberPattern,
                errorLabel: i18next.t('number-validation-message'),
              },
            ],
          };
        }

        if (
          ('TEXTUAL_QUESTIONNAIRE' === fieldType || 'NUMERIC_QUESTIONNAIRE' === fieldType) &&
          fieldGroupField.questions &&
          fieldGroupField.answers
        ) {
          delete field.dataSettings;

          // FIXME: Make questions a real type instead of Record<string, unknown>, also remove the forceAssert in the sort
          const questions = fieldGroupField.questions
            .sort(
              (question1, question2) =>
                forceAssert<{ position: number }>(question1).position -
                forceAssert<{ position: number }>(question2).position
            )
            .map((question) => {
              //FIXME: Once the API provides us with the users translation change this code
              return {
                ...question,
                caption: (question.caption as Caption)?.translations[0].value,
              };
            });

          // FIXME: Make answers a real type instead of Record<string, unknown>, also remove the forceAssert in the sort
          const answers = fieldGroupField.answers
            .sort(
              (answer1, answer2) =>
                forceAssert<{ position: number }>(answer1).position -
                forceAssert<{ position: number }>(answer2).position
            )
            .map((answer) => {
              //FIXME: Once the API provides us with the users translation change this code
              return {
                ...answer,
                caption: answer.caption ? (answer.caption as Caption).translations[0].value : '',
              };
            });

          const isFieldPartiallyRequired = fieldGroupField.fieldValidations
            ? DynamicFormRequiredType.Partial
            : DynamicFormRequiredType.False;

          field.required = fieldGroupField.required ? DynamicFormRequiredType.True : isFieldPartiallyRequired;

          field.dynamicProperties = {
            configuration: {
              id: field.id,
              questions,
              answers,
              allowAttachments: fieldGroupField.allowAttachmentsForQuestions,
              allowRecordLinks: fieldGroupField.allowRecordLinksForQuestions,
              allowObservations: fieldGroupField.allowTextInputWhileAnsweringQuestions,
              displayScores: fieldGroupField.displayScores,
              showFieldCaption: fieldGroupField.showFieldCaption,
              fieldValidations: fieldGroupField.fieldValidations,
              required: field.required,
              type: fieldType,
            },
          };

          if (fieldGroupField.recordLinkFormat) {
            //FIXME: Once the API provides us with the users translation change the buttonCaption code
            field.dynamicProperties.configuration = {
              ...(field.dynamicProperties.configuration as JsonDataItem),
              recordLink: {
                // configuration for the RECORDLINK field as part of the questionnaire field type
                recordLinkFormat: fieldGroupField.recordLinkFormat,
                buttonCaption: fieldGroupField.buttonCaption?.translations[0].value,
                targetModuleFormId: fieldGroupField.targetModuleFormId,
                showSelectButton: fieldGroupField.showSelectButton,
              },
            };
          }
        }

        if ('RISKRATING' === fieldType && fieldGroupField.riskRatingValueDisplay) {
          field.dynamicProperties = {
            riskMatrixId: fieldGroupField.riskMatrixId,
            displayDescription: Boolean(fieldGroupField.displayDescription),
            valueDisplayConfig: fieldGroupField.riskRatingValueDisplay,
          };
        }

        if ('INVITE_BUTTON' === fieldType) {
          //fieldCaption.translations[0].value had no value, so if fieldName is not found add fieldname
          field.fieldName = field.fieldName ?? 'invite_button';
          //FIXME: Once the API provides us with the users translation change this code
          field.dynamicProperties = {
            buttonCaption: fieldGroupField.buttonCaption?.translations[0].value,
          };
          formSettings.globalProperties = { ...formSettings.globalProperties, invitationFieldId: field.id };
        }

        if ('EMAIL_THIS_BUTTON' === fieldType) {
          field.fieldName = field.fieldName ?? 'email_this_button';
          field.dynamicProperties = {
            //TODO Endpoint /forms/simple/{formId} currently returns the buttonCaption as a string
            //If we use /forms/simple/{formId} instead will not need to use translations array
            //same for hint
            buttonCaption: fieldGroupField.buttonCaption?.translations[0].value,
            defaultFieldConfiguration: fieldGroupField.defaultFieldConfiguration,
            hint: fieldGroupField.hint?.translations[0].value,
            keepEmailAudit: Boolean(fieldGroupField.keepEmailAudit),
            includeAttachment: Boolean(fieldGroupField.includeAttachment),
            attachGeneratedDoc: Boolean(fieldGroupField.attachGeneratedDoc),
            emailThisDefaultEmailSubject: fieldGroupField.emailThisDefaultEmailSubject,
            emailThisDefaultEmailBody: fieldGroupField.emailThisDefaultEmailBody,
          };
          formSettings.globalProperties = { ...formSettings.globalProperties, emailThisFieldId: field.id };
        }

        if ('PDF_EXPORT' === fieldType || 'WORD_EXPORT' === fieldType) {
          //FIXME: Once the API provides us with the users translation change this code
          field.fieldName = field.fieldName ?? fieldGroupField?.fieldCaption?.translations[0].value;
          //FIXME: Once the API provides us with the users translation change the buttonCaption and firstWorkflowStep code
          field.dynamicProperties = {
            buttonCaption: fieldGroupField?.buttonCaption?.translations[0].value,
            fieldType: field.fieldType,
            firstWorkflowStep: settings?.initialStep?.label.translations[0].value,
          };
        }

        if ('CLONERECORD' === fieldType) {
          //FIXME: Once the API provides us with the users translation change this code
          field.fieldName = field.fieldName ?? fieldGroupField?.fieldCaption?.translations[0].value;
          //FIXME: Once the API provides us with the users translation change the buttonCaption and firstWorkflowStep code
          field.dynamicProperties = {
            buttonCaption: fieldGroupField?.buttonCaption?.translations[0].value,
            fieldType: field.fieldType,
            firstWorkflowStep: settings?.initialStep?.label.translations[0].value,
            shouldUseCloneWizzard: fieldGroupField.enableBackgroundCopyingUsingWizard,
            cloneWizardTargetPersonField: fieldGroupField.cloneWizardTargetPersonField,
            cloneWizardTargetWorkflowStep: fieldGroupField.cloneWizardTargetWorkflowStep,
            allowUserAttachmentCloning: fieldGroupField.allowUserAttachmentCloning,
            moduleFormCloneTargetId: fieldGroupField.moduleFormCloneTargetId,
          };
        }

        if ('LINK' === fieldType) {
          field.dynamicProperties = {
            target: fieldGroupField?.target,
          };
        }

        if ('MULTISELECTFIELD' === fieldType) {
          field.dynamicProperties = {
            showAddNewOption: fieldGroupField.allowNewValue,
            subType: fieldType,
          };

          // modify type to the 'record' specific optiongroup
          (field.fieldType as unknown as CombinedFieldType) = 'RECORD_MULTISELECTFIELD';
        }

        if ('MULTISELECTCHECKBOX' === fieldType) {
          field.dynamicProperties = {
            multiSelectCheckboxType: fieldGroupField.multiSelectCheckboxType,
            ...(fieldGroupField?.options
              ? {
                  data: fieldGroupField.options.map((option) => {
                    return {
                      [dataProps.valueField]: option.id,
                      [dataProps.textField]: option?.caption?.translations?.[0]?.value ?? '',
                    };
                  }),
                }
              : {}),
          };

          if (!fieldGroupField.options) {
            // modify type to the 'record' specific multiselect-checkbox
            (field.fieldType as unknown as CombinedFieldType) = 'RECORD_MULTISELECTCHECKBOX';
          }
        }

        if ('TWINCOLUMNSELECT' === fieldType || 'TWINCOLUMNSELECTPERSONFIELD' === fieldType) {
          field.dynamicProperties = {
            ...field.dynamicProperties,
            showAddNewOption: fieldGroupField.allowNewValue,
            subType: fieldType,
          };
          (field.fieldType as unknown as CombinedFieldType) = 'RECORD_TWINCOLUMNSELECT';
        }

        if ('AI_FIELD' === fieldType) {
          field.dynamicProperties = {
            aiPrompt: fieldGroupField.aiPrompt,
            aiEndpointURL: fieldGroupField.aiEndpointURL,
            aiInputFieldIds: fieldGroupField.aiInputFieldIds,
            aiOutputFieldIds: fieldGroupField.aiOutputFieldIds,
            buttonCaption: fieldGroupField.buttonCaption?.translations[0].value,
            showFieldCaption: fieldGroupField.showFieldCaption,
            userCanOverrideAiPrompt: fieldGroupField.userCanOverrideAiPrompt,
            formId: settings.id,
            id: fieldGroupField.id,
          };
        }

        // lookup type information
        if (fieldGroupField.lookupType) {
          field.dynamicProperties = {
            ...field.dynamicProperties,
            lookupType: fieldGroupField.lookupType,
            lookupId: fieldGroupField.lookupId,
            lookupRecordId: fieldGroupField.lookupRecordId,
            lookupKeyId: fieldGroupField.lookupKeyId,
            lookupKeyTargetId: fieldGroupField.lookupKeyTargetId,
            lookupModuleFormId: fieldGroupField.lookupModuleFormId,
            lookupHierarchyId: fieldGroupField.lookupHierarchyId,
          };
        }

        // visibiliy information (needs to be last to ensure correct field type is mapped to condition)
        if (fieldGroupField.fieldVisibilities) {
          formSettings.visibilityConditions?.push({
            id: field.id,
            type: field.fieldType,
            groups: [
              {
                conditions: fieldGroupField.fieldVisibilities,
              },
            ],
          });
        }

        groupField?.fields?.push(field);
      }
    }
  }
  return formSettings;
}

/**
 * Find form field from the given form settings field list.
 *
 * @param fields the list of field as specified in the form settings
 * @param fieldName the field key to look for
 *
 * @returns the found field or undefined if not found.
 */
export const findFieldRecursive = (
  fields: Array<DynamicFormField>,
  fieldName: string
): DynamicFormField | undefined => {
  if (!fields) {
    return undefined;
  }

  for (let i = 0, length = fields.length; i < length; i++) {
    if (fields[i].fieldName === fieldName) {
      return fields[i];
    }
    if (fields[i].fields) {
      const foundField = findFieldRecursive(fields[i].fields as Array<DynamicFormField>, fieldName);
      if (foundField) {
        return foundField;
      }
    }
  }
};

export function extractFormGroupFieldsByFieldType(
  formGroupFields?: Array<DynamicFormField>,
  fieldTypes?: Array<CombinedFieldType>
) {
  if (formGroupFields && fieldTypes) {
    return formGroupFields
      .map((formGroupField) =>
        formGroupField.fields?.filter((field) => fieldTypes.includes(field.fieldType as CombinedFieldType))
      )
      .flat();
  }

  return [];
}
